import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';

/**
 * Client-side structured logger for production debugging.
 *
 * The browser can't emit to Cloud Logging directly, so severe events are
 * mirrored into a Firestore `clientErrors/` collection. Admins read; no
 * one else. Each entry carries a session-scoped `traceId` that the
 * server includes on every matching request so a single user action can
 * be followed across the wire.
 *
 * Routine logs still print to the browser console — we're not trying to
 * replace devtools, just capture the failures a prod support engineer
 * needs.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const SESSION_TRACE_ID =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

/** Header name clients send + servers receive. Mirrors GCP conventions. */
export const TRACE_HEADER = 'X-Request-Id';

/** Stable per-tab trace id. Exposed on window for copy-paste into bug reports. */
export function getTraceId(): string {
  return SESSION_TRACE_ID;
}

interface LogPayload {
  level: LogLevel;
  message: string;
  context?: Record<string, unknown>;
  error?: { name: string; message: string; stack?: string };
  requestId?: string;
}

const ERRORS = collection(db, 'clientErrors');

function serializeError(err: unknown): LogPayload['error'] {
  if (err instanceof Error) {
    return { name: err.name, message: err.message, stack: err.stack };
  }
  if (typeof err === 'string') return { name: 'Error', message: err };
  return { name: 'Error', message: JSON.stringify(err).slice(0, 500) };
}

async function sink(payload: LogPayload): Promise<void> {
  if (payload.level !== 'warn' && payload.level !== 'error') return;
  try {
    await addDoc(ERRORS, {
      level: payload.level,
      message: payload.message,
      context: payload.context ?? null,
      error: payload.error ?? null,
      traceId: SESSION_TRACE_ID,
      requestId: payload.requestId ?? null,
      actorUid: auth.currentUser?.uid ?? null,
      actorEmail: auth.currentUser?.email ?? null,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      url: typeof location !== 'undefined' ? location.href : null,
      at: serverTimestamp(),
    });
  } catch {
    // If Firestore is unreachable, still fall through to console.
  }
}

function consoleOut(level: LogLevel, message: string, context?: unknown) {
  const fn =
    level === 'error'
      ? console.error
      : level === 'warn'
        ? console.warn
        : level === 'info'
          ? console.info
          : console.debug;
  fn(`[jam:${level}] ${message}`, context ?? '');
}

export const logger = {
  debug(message: string, context?: Record<string, unknown>) {
    consoleOut('debug', message, context);
  },
  info(message: string, context?: Record<string, unknown>) {
    consoleOut('info', message, context);
  },
  warn(message: string, context?: Record<string, unknown>) {
    consoleOut('warn', message, context);
    void sink({ level: 'warn', message, context });
  },
  error(message: string, err?: unknown, context?: Record<string, unknown>) {
    consoleOut('error', message, { ...context, err });
    void sink({
      level: 'error',
      message,
      context,
      error: err != null ? serializeError(err) : undefined,
    });
  },
};

/**
 * True if the failure is a fetch that got aborted — either by an
 * explicit `AbortController.abort()` or by the browser tearing down the
 * request because the page unloaded mid-flight (SPA route change). Both
 * surface here, not in the actual API surface, so they're not bugs the
 * support engineer needs to see — they're cancellations.
 *
 *   - Explicit aborts: `Error.name === 'AbortError'`.
 *   - Page-unload + fetch tear-down: Chromium/Safari surface
 *     `TypeError: Failed to fetch`; Firefox surfaces
 *     `TypeError: NetworkError when attempting to fetch resource`.
 *
 * A real connectivity failure also produces `TypeError: Failed to fetch`,
 * but the only signal we have at the catch site IS the message string —
 * we can't tell apart "user navigated" from "DNS broke." On a SPA that
 * fires multiple per-page fetches and routes optimistically, the
 * unmount-during-flight case dominates. We accept the tradeoff: skip
 * the warn (don't pollute clientErrors/), let real users + uptime
 * monitoring catch persistent connectivity outages.
 */
export function isAbortError(err: unknown): boolean {
  if (err instanceof Error) {
    if (err.name === 'AbortError') return true;
    if (err.name === 'TypeError') {
      const m = err.message;
      if (m === 'Failed to fetch') return true;
      if (m.includes('NetworkError when attempting to fetch')) return true;
      if (m.includes('Load failed')) return true; // Safari
    }
  }
  return false;
}

/**
 * Convenience wrapper: log a warn only when the error isn't a fetch
 * abort. Use at `useEffect`-mounted data-load catch sites that fire
 * inside a SPA — those frequently abort on route change and shouldn't
 * pollute the operator's clientErrors index.
 */
export function warnUnlessAbort(
  message: string,
  err: unknown,
  context?: Record<string, unknown>,
): void {
  if (isAbortError(err)) return;
  logger.warn(message, { ...(context ?? {}), error: String(err) });
}

/**
 * Wire browser-level error handlers once at app boot.
 * Captures unhandled errors + rejection + fetch failures so support can
 * see what the user saw without asking them to open devtools.
 */
export function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (event) => {
    logger.error('window.onerror', event.error ?? event.message, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    logger.error('unhandledrejection', event.reason);
  });
  // Expose trace id on window for easy copy from devtools.
  (window as unknown as { __jamTraceId: string }).__jamTraceId = SESSION_TRACE_ID;
}
